import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Loader2, UserPlus, UserX, Search, Mail, Phone } from 'lucide-react';
import { searchUsers } from '@/lib/firebase/queries/organizations/searchUsers';
import { getOrganizationManagers } from '@/lib/firebase/queries/organizations/managers';
import { addManagerToOrganization, removeManagerFromOrganization } from '@/lib/firebase/queries/organizations/managers';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Organization } from '@/types/organization';
import type { User } from '@/types';

interface ManagerSelectorProps {
  organization: Organization | null;
  onSuccess?: () => void;
}

export function ManagerSelector({ organization, onSuccess }: ManagerSelectorProps) {
  const [availableManagers, setAvailableManagers] = useState<User[]>([]);
  const [currentManagers, setCurrentManagers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [availableSearchQuery, setAvailableSearchQuery] = useState('');
  const [currentSearchQuery, setCurrentSearchQuery] = useState('');
  const { currentUser } = useAuth();
  const { t, dir } = useLanguage();

  useEffect(() => {
    if (organization) {
      loadManagers();
    }
  }, [organization]);

  const filterManagers = (managers: User[], query: string) => {
    if (!query.trim()) return managers;
    
    const searchTerm = query.toLowerCase();
    return managers.filter(manager => {
      // Every field here is optional-chained on purpose. `User` types email as a
      // required string, but searchUsers builds these objects with a raw
      // `...doc.data()` spread and an `as User[]` cast, so a user document that
      // simply has no email field arrives with `email: undefined`. Reading
      // `.toLowerCase()` off it threw, and because this runs only once the query
      // is non-empty, the whole page went white on the first keystroke.
      const nameEn = `${manager.firstNameEn ?? ''} ${manager.lastNameEn ?? ''}`.toLowerCase();
      const nameAr = `${manager.firstNameAr ?? ''} ${manager.lastNameAr ?? ''}`.toLowerCase();
      const email = manager.email?.toLowerCase() || '';
      const phone = manager.phone?.toLowerCase() || '';
      
      return nameEn.includes(searchTerm) || 
             nameAr.includes(searchTerm) || 
             email.includes(searchTerm) || 
             phone.includes(searchTerm);
    });
  };

  const filteredAvailableManagers = filterManagers(availableManagers, availableSearchQuery);
  const filteredCurrentManagers = filterManagers(currentManagers, currentSearchQuery);

  const loadManagers = async () => {
    if (!organization) return;
    
    try {
      setLoading(true);
      const [available, current] = await Promise.all([
        searchUsers('manager'),
        getOrganizationManagers(organization.id)
      ]);

      // Filter out managers that are already assigned
      const currentIds = new Set(current.map(m => m.id));
      setAvailableManagers(available.filter(m => !currentIds.has(m.id)));
      setCurrentManagers(current);
    } catch (error) {
      console.error('Error loading managers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddManager = async (manager: User) => {
    if (!organization || !currentUser) return;

    try {
      setProcessingId(manager.id);
      const success = await addManagerToOrganization(
        organization.id,
        manager.id,
        currentUser.uid
      );

      if (success) {
        setAvailableManagers(prev => prev.filter(m => m.id !== manager.id));
        setCurrentManagers(prev => [...prev, manager]);
        onSuccess?.();
      }
    } finally {
      setProcessingId(null);
    }
  };

  const handleRemoveManager = async (manager: User) => {
    if (!organization) return;

    try {
      setProcessingId(manager.id);
      const success = await removeManagerFromOrganization(
        organization.id,
        manager.id
      );

      if (success) {
        setCurrentManagers(prev => prev.filter(m => m.id !== manager.id));
        setAvailableManagers(prev => [...prev, manager]);
        onSuccess?.();
      }
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-6" dir={dir}>
      {/* Current Managers */}
      <div className="space-y-4">
        <h3 className="font-medium">{t('dashboard.admin.organizations.members.currentManagers')}</h3>
        
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={dir === 'rtl' ? 'البحث في المديرين الحاليين...' : 'Search current managers...'}
            value={currentSearchQuery}
            onChange={(e) => setCurrentSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <ScrollArea className="h-[400px]">
          <div className="space-y-2 px-4 pb-4">
            {filteredCurrentManagers.map((manager) => (
              <div
                key={manager.id}
                className="flex items-center gap-3 p-3 rounded-lg border"
              >
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    <span className="truncate max-w-[166px]">{manager.email}</span>
                  </div>
                  {manager.phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      <span className="truncate max-w-[100px]">{manager.phone}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">
                    {dir === 'rtl' 
                      ? `${manager.firstNameAr} ${manager.lastNameAr}`
                      : `${manager.firstNameEn} ${manager.lastNameEn}`
                    }
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveManager(manager)}
                  disabled={!!processingId}
                  className="flex-shrink-0 bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700"
                >
                  {processingId === manager.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <UserX className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
            {currentManagers.length === 0 && (
              <div className="text-center py-4 text-muted-foreground">
                                     {t('dashboard.admin.organizations.members.noManagersAssigned')}
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Available Managers */}
      <div className="space-y-4">
        <h3 className="font-medium">{t('dashboard.admin.organizations.members.availableManagers')}</h3>
        
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={dir === 'rtl' ? 'البحث في المديرين المتاحين...' : 'Search available managers...'}
            value={availableSearchQuery}
            onChange={(e) => setAvailableSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <ScrollArea className="h-[400px]">
          <div className="space-y-2 px-4 pb-4">
            {filteredAvailableManagers.map((manager) => (
              <div
                key={manager.id}
                className="flex items-center gap-3 p-3 rounded-lg border"
              >
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    <span className="truncate max-w-[166px]">{manager.email}</span>
                  </div>
                  {manager.phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      <span className="truncate max-w-[100px]">{manager.phone}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">
                    {dir === 'rtl' 
                      ? `${manager.firstNameAr} ${manager.lastNameAr}`
                      : `${manager.firstNameEn} ${manager.lastNameEn}`
                    }
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleAddManager(manager)}
                  disabled={!!processingId}
                  className="flex-shrink-0 bg-green-50 hover:bg-green-100 text-green-600 hover:text-green-700"
                >
                  {processingId === manager.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <UserPlus className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
            {availableManagers.length === 0 && (
              <div className="text-center py-4 text-muted-foreground">
                                     {t('dashboard.admin.organizations.members.noManagersAvailable')}
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}